import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";

const input = "mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";

export default function PortalLogin() {
  const { user, loading, login, setSession } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user?.role === "patient") return <Navigate to="/portal" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        const data = await login(form.email, form.password);
        if (data.role !== "patient") {
          toast.info("That's a provider account — taking you to the provider portal.");
          navigate("/admin");
          return;
        }
      } else {
        const { data } = await api.post("/auth/register", { name: form.name, email: form.email, phone: form.phone, password: form.password });
        setSession(data);
        toast.success("Welcome to Releaf! Your patient account is ready.");
      }
      navigate("/portal");
    } catch (err) {
      setError(apiError(err, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="portal-login-page" className="bg-ivory px-6 pb-28 pt-16">
      <div className="mx-auto max-w-md">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">Patient Portal</p>
        <h1 className="mt-4 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-3 text-sm text-moss">
          See your appointments, signed forms, memberships and progress notes in one place.
        </p>

        <div className="mt-8 flex rounded-md border border-linen p-1 text-sm font-semibold">
          <button type="button" data-testid="portal-mode-login" onClick={() => setMode("login")} className={`flex-1 rounded px-4 py-2 transition-colors ${mode === "login" ? "bg-forest text-ivory" : "text-moss hover:text-forest"}`}>Sign In</button>
          <button type="button" data-testid="portal-mode-register" onClick={() => setMode("register")} className={`flex-1 rounded px-4 py-2 transition-colors ${mode === "register" ? "bg-forest text-ivory" : "text-moss hover:text-forest"}`}>Create Account</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-5">
          {mode === "register" && (
            <>
              <div>
                <label htmlFor="portal-name" className={label}>Full name</label>
                <input id="portal-name" data-testid="portal-name-input" required value={form.name} onChange={set("name")} className={input} autoComplete="name" />
              </div>
              <div>
                <label htmlFor="portal-phone" className={label}>Phone</label>
                <input id="portal-phone" data-testid="portal-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={input} autoComplete="tel" />
              </div>
            </>
          )}
          <div>
            <label htmlFor="portal-email" className={label}>Email</label>
            <input id="portal-email" data-testid="portal-email-input" type="email" required value={form.email} onChange={set("email")} className={input} autoComplete="email" />
            {mode === "register" && <p className="mt-1.5 text-xs text-moss">Use the same email you book with so your visits appear automatically.</p>}
          </div>
          <div>
            <label htmlFor="portal-password" className={label}>Password</label>
            <input id="portal-password" data-testid="portal-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} className={input} autoComplete={mode === "login" ? "current-password" : "new-password"} />
            {mode === "register" && <p className="mt-1.5 text-xs text-moss">At least 8 characters.</p>}
          </div>
          {error && <p data-testid="portal-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
          <button type="submit" data-testid="portal-submit" disabled={busy} className="w-full rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:opacity-50">
            {busy ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="mt-8 text-sm text-moss">
          <Link to="/reset-password" data-testid="portal-forgot-password-link" className="font-semibold text-forest hover:underline">Forgot your password?</Link>
        </p>
        <p className="mt-3 text-sm text-moss">
          Are you the provider? <Link to="/admin/login" data-testid="portal-provider-link" className="font-semibold text-forest hover:underline">Provider login</Link>
        </p>
      </div>
    </main>
  );
}
